import MergeField from './MergeField';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, SuccessButton, WarningButton } from '../connectedForm/Button';
import { HeaderDiv, SelectButton } from './MergeConflictStyledComponents';
import { change, mergeConflicts, submit } from '../connectedForm/utils/actions';
import { useDispatch } from 'react-redux';
import { AlertType, Maybe } from '../../graphql/schema/p4types';
import validation from '../../graphql/schema/validation.json';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../modals/Styles';
import getIn from '../connectedForm/utils/getIn';
import { EntityType } from '../../@types/entityTypes';
import { miscArrayEqual } from '../../utils/ConcurrencyUtils';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { Tooltip } from '@fluentui/react-components';
import FluentModal from '../modals/FluentModal';

export type IMergeConflict = {
  formValue: any;
  serverValue: any;
  path: string;
};

enum Validation {
  Success = 'SUCCESS',
  Fail = 'FAIL',
  Unavailable = 'UNAVAILABLE',
}

type IMergeConflictsProps = {
  form: string;
  conflicts: Array<IMergeConflict>;
  title: string;
  entity: EntityType;
  baseValue: any;
};

enum ServerManagedFieldBehaviour {
  Hide,
  Lock,
  None,
}

/**
 * A map of fields managed by the server and how to handle them.
 * None - Not a server-managed field.
 * Hide - Avoid including this field in the conflict list.
 * Lock - Show this field in the conflict list, but do not allow it to be modified.
 */
const SERVER_MANAGED_FIELDS: { [key: string]: ServerManagedFieldBehaviour } = {
  concurrencyVersion: ServerManagedFieldBehaviour.Hide,
  updatedAt: ServerManagedFieldBehaviour.Hide,
  updatedBy: ServerManagedFieldBehaviour.Hide,
  documentStatus: ServerManagedFieldBehaviour.Lock,
  orderType: ServerManagedFieldBehaviour.Lock,

  /**
   * Since they are generated, order status should be generally safe, however the fact it's changed is a significant red flag so locking it should draw attention to the risk.
   */
  status: ServerManagedFieldBehaviour.Lock,
  salesOrderStatus: ServerManagedFieldBehaviour.Lock,
  salesOrderLineStatus: ServerManagedFieldBehaviour.Lock,
  purchaseOrderLineStatus: ServerManagedFieldBehaviour.Lock,

  /**
   * Any fields here are locked because they should not be reset by users under any circumstance.
   * (Note - 'received' is here while 'allocated', etc are not because those fields will be re-calculated by status updates anyway).
   */
  received: ServerManagedFieldBehaviour.Lock,
  pickRequired: ServerManagedFieldBehaviour.Lock,

  // Lock customer and supplier balance fields
  balance: ServerManagedFieldBehaviour.Lock,
  future: ServerManagedFieldBehaviour.Lock,
  current: ServerManagedFieldBehaviour.Lock,
  month1: ServerManagedFieldBehaviour.Lock,
  month2: ServerManagedFieldBehaviour.Lock,
  month3: ServerManagedFieldBehaviour.Lock,
  month4: ServerManagedFieldBehaviour.Lock,
  highestBalance: ServerManagedFieldBehaviour.Lock,
  lastTransactionDate: ServerManagedFieldBehaviour.Lock,
  lastPaymentDate: ServerManagedFieldBehaviour.Lock,
};

/**
 * Check if this value is server-managed, i.e. 'updatedAt', 'updatedBy' (These values are updated on persist, not by user entry).
 * These values will be displayed in merge conflicts but cannot be modified.
 */
const getServerManagedValueBehaviour = (path: string): ServerManagedFieldBehaviour => {
  const splitPath = path.split('.');
  const managedFieldKey = Object.keys(SERVER_MANAGED_FIELDS).find(
    (blackListField) => splitPath[splitPath.length - 1] === blackListField
  );

  return (
    (managedFieldKey && (SERVER_MANAGED_FIELDS[managedFieldKey] as ServerManagedFieldBehaviour)) ??
    ServerManagedFieldBehaviour.None
  );
};

const isConcurrencyVersion = (path: string) => {
  const splitPath = path.split('.');
  return splitPath[splitPath.length - 1] === 'concurrencyVersion';
};

/**
 * Validate conflicting merge types
 */
const validateType = (conflict: IMergeConflict, entity: EntityType) => {
  let typeAtLevel = (validation as { [key: string]: any })[entity.type];
  const splitPath = conflict.path.split('.');
  for (let i = 0; i < splitPath.length; i++) {
    if (typeAtLevel && typeAtLevel[splitPath[i]]) {
      const typeThisLevel = typeAtLevel[splitPath[i]];
      if (typeThisLevel.isTransient || typeThisLevel.isCalculated) {
        return Validation.Fail;
      } else if (typeThisLevel.type === 'List') {
        i++; //Skip over list index element for next loop.
        typeAtLevel = (validation as { [key: string]: any })[typeThisLevel.typeParameters[0]]; //Need to use typeParameters to get list element type (assuming there's only ever 1..).
      } else {
        typeAtLevel = (validation as { [key: string]: any })[typeThisLevel.type];
      }
    } else {
      //Couldn't find a type, return unavailable.
      return Validation.Unavailable;
    }
  }

  //No types were transient or calculated - return success.
  return Validation.Success;
};

type ILocalMergeFunction = (conflicts: Array<any>, baseValue: any) => void;

/**
 * Local merge capability so that when an asynchronous process changes server values for a form, we can directly merge them into the UI without requiring a re-save
 * to skip over any user requirement of manually resolving conflicts in this scenario.
 */
export const useLocalMerge: (params: { form: string; entity: EntityType }) => ILocalMergeFunction = ({
  form,
  entity,
}) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const dispatch = useDispatch();

  const { dispatchSilentToast } = useP4ToastController();

  return useCallback(
    (conflicts, baseValues) => {
      let hasMerged = false;
      let newConcurrencyVersion;
      for (const conflict of conflicts) {
        const validationResult: Validation =
          (entity && (validation as { [key: string]: any })[entity.type] && validateType(conflict, entity)) ||
          Validation.Unavailable;
        const baseValue = getIn(baseValues, conflict.path);

        //Update appropriate fields - Any that don't pass this check are ignored (Because the user changed them, or they didn't conflict so must be the same anyway).
        if (conflict.path === 'concurrencyVersion') {
          // Root concurrencyVersion must be deferred - Pristine entity check resets base values as soon as it is changed.
          //  - So if we trigger it early the form may be seen as 'dirty'
          newConcurrencyVersion = conflict.serverValue;
        } else if (
          isConcurrencyVersion(conflict.path) ||
          baseValue === conflict.formValue ||
          (Array.isArray(baseValue) && miscArrayEqual(baseValue, conflict.formValue)) ||
          validationResult === Validation.Fail
        ) {
          //If this is a concurrency version field, we can directly update it.
          //If this field has not been changed in the UI, we can directly update it.
          //If this field has a validation result of 'Fail' - In this context it just means it was a transient or calculated field - We can just take the server copy of these
          // and merge them in - Typically they are re-calculated fields from the services anyway.
          dispatch(change(form, conflict.path, conflict.serverValue));
          hasMerged = true;
        }
      }
      if (newConcurrencyVersion !== undefined) {
        dispatch(change(form, 'concurrencyVersion', newConcurrencyVersion));
      }
      if (hasMerged) {
        //Use a silent toast to avoid unnecessary user confusion (But still allow us to validate that the auto conflict has triggered in notification history).
        dispatchSilentToast(
          'Auto Conflict Resolution',
          'One or more server values have changed and form values have been automatically merged',
          AlertType.Info
        );
      } else {
        console.warn('Local merge triggered but no conflicts were found.');
      }
    },
    [dispatch, dispatchSilentToast, entity, form]
  );
};

const MergeConflicts = ({ form, conflicts, title, entity, baseValue }: IMergeConflictsProps) => {
  const [mergeList, setMergeList] = useState<Maybe<Array<ReactElement>>>(null);
  const [hasMergeableFields, setHasMergeableFields] = useState<boolean>(false);

  const { dispatchSimpleToast } = useP4ToastController();

  const dispatch = useDispatch();

  useEffect(() => {
    const newMergeList = createList(conflicts);

    if (newMergeList.length < 1) {
      dispatchSimpleToast(
        'Conflict Resolution',
        'Conflicting entities only differ on concurrency, re-saving...',
        AlertType.Info
      );
      confirm();
    }
    setMergeList(newMergeList);

    // didMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = () => {
    Promise.resolve(dispatch(submit(form))).then(() => {
      dispatch(mergeConflicts(form, []));
    });
  };

  const onClose = () => {
    for (const conflict of conflicts) {
      if (
        isConcurrencyVersion(conflict.path) &&
        getServerManagedValueBehaviour(conflict.path) === ServerManagedFieldBehaviour.None
      ) {
        updateStore(conflict.path, conflict.formValue);
      }
    }
    dispatch(mergeConflicts(form, []));
  };

  const updateStore = (path: string, value: any) => {
    dispatch(change(form, path, value));
  };

  const updateSelection = (value: 'formValue' | 'serverValue') => {
    for (const conflict of conflicts) {
      if (
        !isConcurrencyVersion(conflict.path) &&
        getServerManagedValueBehaviour(conflict.path) === ServerManagedFieldBehaviour.None
      ) {
        updateStore(conflict.path, conflict[value]);
      }
    }
  };

  /**
   * Validate conflicting merge types
   */
  const validateType = (conflict: IMergeConflict) => {
    let typeAtLevel = (validation as { [key: string]: any })[entity.type];
    const splitPath = conflict.path.split('.');
    for (let i = 0; i < splitPath.length; i++) {
      if (typeAtLevel[splitPath[i]]) {
        const typeThisLevel = typeAtLevel[splitPath[i]];
        if (typeThisLevel.isTransient || typeThisLevel.isCalculated) {
          return Validation.Fail;
        } else if (typeThisLevel.type === 'List') {
          i++; //Skip over list index element for next loop.
          typeAtLevel = (validation as { [key: string]: any })[typeThisLevel.typeParameters[0]]; //Need to use typeParameters to get list element type (assuming there's only ever 1..).
        } else {
          typeAtLevel = (validation as { [key: string]: any })[typeThisLevel.type];
        }
      } else {
        //Couldn't find a type, return unavailable.
        return Validation.Unavailable;
      }
    }

    //No types were transient or calculated - return success.
    return Validation.Success;
  };

  const updatedByUser = useMemo(() => {
    return conflicts.find((value) => value.path.endsWith('updatedBy'));
  }, [conflicts]);

  const createList = (conflicts: Array<IMergeConflict>) => {
    const newMergeList: Array<ReactElement> = [];
    setHasMergeableFields(false);
    for (const conflict of conflicts) {
      const serverManagedBehaviour = getServerManagedValueBehaviour(conflict.path);
      if (serverManagedBehaviour === ServerManagedFieldBehaviour.Hide) {
        updateStore(conflict.path, conflict.serverValue);
      } else if (serverManagedBehaviour === ServerManagedFieldBehaviour.Lock) {
        updateStore(conflict.path, conflict.serverValue);
        newMergeList.push(
          <MergeField
            conflict={conflict}
            updateStore={updateStore}
            form={form}
            formValueHasChanged={false}
            locked={true}
          />
        );
      } else {
        setHasMergeableFields(true);
        const formValueHasChanged = getIn(baseValue, conflict.path) !== conflict.formValue;

        const validationResult: Validation =
          (entity && (validation as { [key: string]: any })[entity.type] && validateType(conflict)) ||
          Validation.Unavailable;

        if (validationResult === Validation.Success) {
          //If we couldn't determine type, or if the field is valid (Neither calculated nor transient) then add the merge field.
          newMergeList.push(
            <MergeField
              conflict={conflict}
              updateStore={updateStore}
              form={form}
              formValueHasChanged={formValueHasChanged}
            />
          );
        } else if (validationResult === Validation.Unavailable) {
          console.warn('Unable to validate conflict at path', conflict.path);
          newMergeList.push(
            <MergeField
              conflict={conflict}
              updateStore={updateStore}
              form={form}
              formValueHasChanged={formValueHasChanged}
            />
          );
        }
      }
    }
    return newMergeList;
  };

  if (!mergeList) return null;

  return (
    <FluentModal
      isOpen={true}
      styles={{
        main: { minWidth: '1200px', paddingBottom: '60px', overflowY: 'unset' },
        scrollableContent: { maxHeight: '97vh' },
      }}
    >
      <BasicHeader
        secondaryTitle={
          <div style={{ paddingLeft: '16px' }}>
            <li>Please choose the correct value for each field.</li>
            <li>
              The left (your) value is the default (unless it is a locked field), the <b>bold</b> value is recommended.
            </li>
            <li>If uncertain please refresh the page and re-enter your changes.</li>
          </div>
        }
      >
        {updatedByUser
          ? `${updatedByUser} has also altered this record, conflicts need to be resolved.`
          : 'You have already modified this record, conflicts need to be resolved'}
      </BasicHeader>
      <div style={{ padding: '1em', paddingBottom: '65px' }}>
        <HeaderDiv>
          <SelectButton>Name</SelectButton>
        </HeaderDiv>
        <HeaderDiv>
          <SuccessButton
            data-testid="selectAll-yours"
            onClick={updateSelection.bind(null, 'formValue')}
            disabled={!hasMergeableFields}
            styles={{
              root: {
                height: '25px',
                minHeight: '25px',
                position: 'relative',
              },
              wrapper: {
                padding: 0,
                lineHeight: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            Select All From Form
          </SuccessButton>
        </HeaderDiv>
        <HeaderDiv>
          <SuccessButton
            data-testid="selectAll-server"
            onClick={updateSelection.bind(null, 'serverValue')}
            disabled={!hasMergeableFields}
            styles={{
              root: {
                height: '25px',
                minHeight: '25px',
                position: 'relative',
              },
              wrapper: {
                padding: 0,
                lineHeight: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            Select All From Server
          </SuccessButton>
        </HeaderDiv>
        <HeaderDiv>
          <SelectButton>Selected</SelectButton>
        </HeaderDiv>
        {mergeList}
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="mergeConflicts-no" onClick={onClose}>
          Cancel
        </Button>
        <Tooltip
          content={
            'Submit amendments. Please consider carefully and consider refreshing the page/re-entering changes in cases of uncertainty.'
          }
          relationship={'label'}
        >
          <div>
            <WarningButton data-testid="mergeConflicts-yes" onClick={confirm}>
              Submit
            </WarningButton>
          </div>
        </Tooltip>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default memo(MergeConflicts);
